import React from "react";
import { DragDropContext, Draggable, DropResult, Droppable } from "react-beautiful-dnd";

import { ensure } from "../../../utils";
import { Theme } from "../../theme/Theme";

export interface CheckboxListItem {
    key: number;
    text: string;
    checked: boolean;
    disabled?: boolean;
}

interface DraggableCheckboxListProps {
    items: CheckboxListItem[];
    setItems(items: CheckboxListItem[]): void;
}

export const DraggableCheckboxList: React.FC<DraggableCheckboxListProps> = (props: DraggableCheckboxListProps) => {
    const { items, setItems } = props;

    const handleCheckboxChange = (key: number) => {
        const newItems = items.map((item) => (item.key === key ? { ...item, checked: !item.checked } : item));

        setItems(newItems);
    };

    const onDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        } else {
            const reorderedList = reorder(items, result.source.index, result.destination.index);

            setItems(reorderedList);
        }
    };

    const reorder = (list: CheckboxListItem[], startIndex: number, endIndex: number) => {
        const disabledIndexes = getDisabledIndexes(list);

        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        const enabledItems = result.filter((i) => !i.disabled);

        disabledIndexes.forEach((i) => enabledItems.splice(i, 0, items[i]));

        return enabledItems;
    };

    const recordStyle = { padding: "10px", border: "1px solid #ccc", marginBottom: "5px" };

    return (
        <>
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable">
                    {(droppableProvided) => (
                        <div {...droppableProvided.droppableProps} ref={droppableProvided.innerRef}>
                            {items.map((item, index) => (
                                <Draggable
                                    key={item.key}
                                    draggableId={String(item.key)}
                                    index={index}
                                    isDragDisabled={item.disabled}
                                    shouldRespectForcePress={item.disabled}
                                >
                                    {(draggableProvided) => (
                                        <div
                                            ref={draggableProvided.innerRef}
                                            {...draggableProvided.draggableProps}
                                            {...draggableProvided.dragHandleProps}
                                            style={{
                                                margin: "0px 3px 5px 3px",
                                                padding: 5,
                                                backgroundColor: Theme.palette.white,
                                                ...draggableProvided.draggableProps.style
                                            }}
                                        >
                                            <div style={recordStyle} key={item.key}>
                                                <input
                                                    type="checkbox"
                                                    checked={item.checked}
                                                    disabled={item.disabled}
                                                    onChange={() => handleCheckboxChange(item.key)}
                                                />
                                                <label style={{ marginLeft: "10px" }}>{item.text}</label>
                                            </div>
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {droppableProvided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </>
    );
};

const getDisabledIndexes = (list: CheckboxListItem[]): number[] => {
    return list
        .map((item, index) => (item.disabled ? index : undefined))
        .filter((index) => index !== undefined)
        .map((i) => {
            ensure(i);
            return i;
        });
};
